import {
  createContext,
  type ReactNode,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

const SIDEBAR_STORAGE_KEY = "fiscalo_portal_sidebar_open";

const MOBILE_BREAKPOINT_PX = 1024;

function getStoredSidebarOpen(): boolean {
  if (typeof window === "undefined") return true;
  if (window.innerWidth < MOBILE_BREAKPOINT_PX) return false;
  try {
    const stored = localStorage.getItem(SIDEBAR_STORAGE_KEY);
    return stored !== "false";
  } catch {
    return true;
  }
}

interface SidebarContextValue {
  isSidebarOpen: boolean;
  toggleSidebar: () => void;
  closeSidebar: () => void;
}

const SidebarContext = createContext<SidebarContextValue | undefined>(
  undefined
);

export function SidebarProvider({
  children,
}: {
  children: ReactNode;
}): JSX.Element {
  const [isSidebarOpen, setSidebarOpen] = useState(getStoredSidebarOpen);

  useEffect(() => {
    try {
      localStorage.setItem(SIDEBAR_STORAGE_KEY, String(isSidebarOpen));
    } catch {
      // ignore when localStorage is unavailable (e.g. private browsing)
    }
  }, [isSidebarOpen]);

  useEffect(() => {
    const handleResize = (): void => {
      if (window.innerWidth < MOBILE_BREAKPOINT_PX) setSidebarOpen(false);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const value = useMemo<SidebarContextValue>(
    () => ({
      isSidebarOpen,
      toggleSidebar: () => setSidebarOpen((prev) => !prev),
      closeSidebar: () => setSidebarOpen(false),
    }),
    [isSidebarOpen]
  );

  return (
    <SidebarContext.Provider value={value}>{children}</SidebarContext.Provider>
  );
}

export function useSidebar(): SidebarContextValue {
  const context = useContext(SidebarContext);
  if (!context) {
    throw new Error("useSidebar must be used within a SidebarProvider");
  }
  return context;
}
